'use client';

import React from 'react';
import { Shield, Lock, Eye, FileText, UserCheck, Bell } from 'lucide-react';

const PrivacyPolicy = () => {
  const sections = [
    {
      icon: <UserCheck className="w-6 h-6 text-blue-600" />,
      title: '1. Thu thập thông tin cá nhân',
      content: [
        'Chúng tôi thu thập thông tin cá nhân khi bạn đăng ký khóa học, điền vào biểu mẫu liên hệ hoặc đăng ký nhận bản tin.',
        'Thông tin bao gồm: Họ tên, số điện thoại, email, ngày sinh của học viên và thông tin liên lạc của phụ huynh/người giám hộ.',
        'Chúng tôi cũng có thể thu thập thông tin về tình trạng sức khỏe liên quan đến khả năng vận động dưới nước để đảm bảo an toàn cho học viên.',
      ],
    },
    {
      icon: <Eye className="w-6 h-6 text-blue-600" />,
      title: '2. Sử dụng thông tin',
      content: [
        'Để quản lý và tổ chức các lớp học bơi phù hợp với trình độ của học viên.',
        'Để liên lạc, thông báo về lịch học, thay đổi giờ học hoặc các sự kiện đặc biệt của CLB.',
        'Để cải thiện chất lượng dịch vụ và trải nghiệm người dùng trên website.',
        'Để đảm bảo an toàn và xử lý các tình huống khẩn cấp trong quá trình huấn luyện.',
      ],
    },
    {
      icon: <Lock className="w-6 h-6 text-blue-600" />,
      title: '3. Bảo mật thông tin',
      content: [
        'Chúng tôi áp dụng các biện pháp bảo mật kỹ thuật và tổ chức để bảo vệ dữ liệu cá nhân của bạn khỏi việc truy cập trái phép, mất mát hoặc thay đổi.',
        'Dữ liệu chỉ được tiếp cận bởi nhân viên có thẩm quyền và huấn luyện viên trực tiếp quản lý lớp học.',
      ],
    },
    {
      icon: <Shield className="w-6 h-6 text-blue-600" />,
      title: '4. Chia sẻ thông tin',
      content: [
        'CLB Bơi Lặn Thủ Đô cam kết không bán, trao đổi hoặc cho thuê thông tin cá nhân của bạn cho bên thứ ba vì mục đích thương mại.',
        'Thông tin chỉ được chia sẻ trong trường hợp có yêu cầu từ cơ quan pháp luật hoặc để bảo vệ an toàn tính mạng của học viên trong tình huống khẩn cấp.',
      ],
    },
    {
      icon: <FileText className="w-6 h-6 text-blue-600" />,
      title: '5. Quyền của người dùng',
      content: [
        'Bạn có quyền yêu cầu truy cập, chỉnh sửa hoặc xóa thông tin cá nhân của mình bất kỳ lúc nào.',
        'Bạn có thể từ chối nhận các thông báo tiếp thị bằng cách làm theo hướng dẫn trong email hoặc liên hệ trực tiếp với chúng tôi.',
      ],
    },
    {
      icon: <Bell className="w-6 h-6 text-blue-600" />,
      title: '6. Thay đổi chính sách',
      content: [
        'Chúng tôi có quyền cập nhật chính sách bảo mật này để phản ánh những thay đổi trong cách chúng tôi xử lý thông tin.',
        'Các thay đổi sẽ có hiệu lực ngay khi được đăng tải trên website này.',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Section */}
      <section className="relative py-20 bg-blue-600 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg
            className="w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" />
          </svg>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Chính Sách Bảo Mật
          </h1>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto italic">
            &quot;Tại CLB Bơi Lặn Thủ Đô, chúng tôi tôn trọng và cam kết bảo vệ
            quyền riêng tư của bạn.&quot;
          </p>
          <div className="mt-8 inline-flex items-center text-blue-200 text-sm">
            <span>
              Cập nhật lần cuối: {new Date().toLocaleDateString('vi-VN')}
            </span>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 p-8 md:p-12">
            <div className="space-y-12">
              {sections.map((section, index) => (
                <div key={index} className="flex flex-col md:flex-row gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center border border-blue-100 shadow-sm">
                      {section.icon}
                    </div>
                  </div>
                  <div className="flex-grow">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      {section.title}
                    </h2>
                    <ul className="space-y-3">
                      {section.content.map((item, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-3 text-gray-600 leading-relaxed text-lg"
                        >
                          <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-2.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact Footer */}
            <div className="mt-16 pt-12 border-t border-gray-100 text-center">
              <p className="text-gray-500 mb-4">
                Mọi thắc mắc về chính sách bảo mật, vui lòng liên hệ:
              </p>
              <div className="inline-flex flex-col md:flex-row items-center gap-6 justify-center">
                <div className="flex items-center gap-2 text-blue-600 font-semibold">
                  <span>Hotline: +84 968 156 663</span>
                </div>
                {/* <div className="flex items-center gap-2 text-blue-600 font-semibold">
                  <span>Email: lienhe@clbthudo.com</span>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
